import React, {useRef, useState} from 'react';
import {view, Icon, Tooltip, tokens, css, useResizeObserver} from '@sail/ui';
import {chevronRight} from '@sail/icons/react/Icon';
import {isMobile} from 'src/utils/utils';

const selectedBorderColor = tokens.color.neutral[500].toString();

interface SelectionCardProps {
  icon: React.ReactNode;
  label: string;
  sublabel?: string;
  accessory?: React.ReactNode;
  selected?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

export const SelectionCard = ({
  icon,
  label,
  sublabel,
  accessory,
  selected = false,
  disabled = false,
  onSelect,
}: SelectionCardProps) => {
  const [isSublabelTruncated, setIsSublabelTruncated] = useState(false);
  // Observe the stable column container (the sublabel node is recreated when it
  // toggles in/out of the Tooltip) and re-measure the sublabel on resize.
  const columnRef = useRef<HTMLDivElement>(null);
  const sublabelRef = useRef<HTMLDivElement>(null);

  useResizeObserver({
    node: columnRef,
    handler: () => {
      const el = sublabelRef.current;
      if (el) {
        setIsSublabelTruncated(el.scrollWidth > el.clientWidth);
      }
    },
  });

  const sublabelContent = sublabel ? (
    <view.div
      ref={sublabelRef}
      css={{
        font: 'label.small',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: 'fill',
        minWidth: 'space.0',
        color: tokens.color.neutral[400].toString(),
      }}
    >
      {sublabel}
    </view.div>
  ) : null;

  return (
    <view.button
      role="option"
      aria-selected={selected}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={onSelect}
      css={{
        stack: 'x',
        alignItems: 'center',
        gap: 'small',
        padding: 'space.150',
        borderRadius: 'medium',
        borderWidth: 'small',
        borderStyle: 'solid',
        borderColor: selected ? selectedBorderColor : 'border',
        // box-shadow instead of border-width to avoid layout shift on selection
        boxShadow: selected ? `0 0 0 1px ${selectedBorderColor}` : 'none',
        backgroundColor: 'transparent',
        transition:
          'border-color 150ms ease, box-shadow 150ms ease, background-color 150ms ease',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        width: 'fill',
        ':hover': disabled
          ? {}
          : {
              borderColor: selectedBorderColor,
              boxShadow: `0 0 0 1px ${selectedBorderColor}`,
              backgroundColor: 'offset',
            },
      }}
    >
      {icon}
      <view.div
        css={{
          stack: 'x',
          alignItems: 'center',
          distribute: 'space-between',
          gap: 'small',
          flex: 1,
          minWidth: 'space.0',
        }}
      >
        <view.div
          ref={columnRef}
          css={{stack: 'y', flex: 1, minWidth: 'space.0', textAlign: 'start'}}
        >
          <view.span
            css={{
              font: 'label.medium.emphasized',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: 'text',
            }}
          >
            {label}
          </view.span>
          {sublabelContent &&
            (!isMobile() && isSublabelTruncated ? (
              <Tooltip
                trigger={sublabelContent}
                delay={300}
                placement="bottom start"
                uses={[css({textAlign: 'start', padding: 'small'})]}
              >
                {sublabel}
              </Tooltip>
            ) : (
              sublabelContent
            ))}
        </view.div>
        <view.div css={{stack: 'x', gap: 'small', alignItems: 'center'}}>
          <view.div css={{font: 'label.small', color: 'subdued'}}>
            {accessory}
          </view.div>
          <Icon icon={chevronRight} size="xsmall" />
        </view.div>
      </view.div>
    </view.button>
  );
};
